import { cn } from '@/lib/utils';

interface StatusBadgeProps {
  status: string;
  className?: string;
}

const statusConfig: Record<string, { label: string; classes: string }> = {
  active: { label: 'Active', classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  registered: { label: 'Registered', classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  paused: { label: 'Paused', classes: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  error: { label: 'Error', classes: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  failed: { label: 'Failed', classes: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  stopped: { label: 'Stopped', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  deregistered: { label: 'Deregistered', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  completed: { label: 'Completed', classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  queued: { label: 'Queued', classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  running: { label: 'Running', classes: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  delayed: { label: 'Delayed', classes: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
  cancelled: { label: 'Cancelled', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  disabled: { label: 'Disabled', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  healthy: { label: 'Healthy', classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  degraded: { label: 'Degraded', classes: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  down: { label: 'Down', classes: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  pending: { label: 'Pending', classes: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  ended: { label: 'Ended', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  // Repository indexer statuses
  none: { label: 'None', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  indexing: { label: 'Indexing', classes: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  started: { label: 'Started', classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  archived: { label: 'Archived', classes: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400' },
  admin: { label: 'Admin', classes: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  user: { label: 'User', classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  operator: { label: 'Operator', classes: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' },
  viewer: { label: 'Viewer', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  https: { label: 'HTTPS', classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  ssh: { label: 'SSH', classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  // Storage statuses
  assembled: { label: 'Assembled', classes: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400' },
  assembling: { label: 'Assembling', classes: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  replicating: { label: 'Replicating', classes: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  available: { label: 'Available', classes: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  partial: { label: 'Partial', classes: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
  invalid: { label: 'Invalid', classes: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  deleting: { label: 'Deleting', classes: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  purged: { label: 'Purged', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  uploading: { label: 'Uploading', classes: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  aborted: { label: 'Aborted', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  unknown: { label: 'Unknown', classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
};

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const key = typeof status === 'string' && status.trim() ? status.toLowerCase() : 'unknown';
  const config = statusConfig[key] ?? {
    label: status ?? 'Unknown',
    classes: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        config.classes,
        className,
      )}
    >
      {config.label}
    </span>
  );
}
